import { useState, useRef, useCallback } from "react";
import {
  Upload, Button, Table, Alert, Space, Typography, Tag, Tooltip,
  App, Statistic, Row, Col, Card, Divider, Select
} from "antd";
import {
  UploadOutlined, CheckCircleOutlined, WarningOutlined,
  CloseCircleOutlined, ImportOutlined, EditOutlined, LinkOutlined
} from "@ant-design/icons";
import type { UploadFile } from "antd";
import { useNavigate } from "react-router";
import { API_BASE_URL } from "../../utils/constants";
import type { ImportPreviewRow, ImportPreviewResponse } from "../../types/farm-production";
import { usePageTitle } from "../../hooks";

const { Title, Text } = Typography;

type FilterMode = "all" | "issues";

interface FarmOption {
  id: number;
  name: string;
  display_name: string;
  producer: { id: number; name: string };
}

// Map of "externalFarmId-seasonYear" → { farmId, farmName, producerName }
interface FarmOverride {
  farmId: number;
  farmName: string;
  producerName: string;
}

export const FarmProductionImport = () => {
  usePageTitle("Importar Producción");
  const navigate = useNavigate();
  const { message, modal } = App.useApp();

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);
  const [jsonData, setJsonData] = useState<object[] | null>(null);

  // Manual farm overrides: key = "externalFarmId-seasonYear"
  const [farmOverrides, setFarmOverrides] = useState<Record<string, FarmOverride>>({});

  // Farm search state
  const [farmOptions, setFarmOptions] = useState<FarmOption[]>([]);
  const [farmSearchLoading, setFarmSearchLoading] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleFileRead = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target?.result as string);
        if (!Array.isArray(parsed)) {
          message.error("El archivo debe contener un array JSON");
          return;
        }
        setJsonData(parsed);
        setPreview(null);
        setFarmOverrides({});
      } catch {
        message.error("Error al parsear el JSON");
      }
    };
    reader.readAsText(file);
    return false;
  };

  const handlePreview = async () => {
    if (!jsonData) return;
    setLoadingPreview(true);
    try {
      const token = localStorage.getItem("access_token") || "";
      const res = await fetch(`${API_BASE_URL}/farm-production-records/preview-import/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(jsonData),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: ImportPreviewResponse = await res.json();
      setPreview(data);
      setFarmOverrides({});
    } catch (err) {
      message.error("Error al previsualizar: " + String(err));
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleConfirmImport = () => {
    if (!jsonData || !preview) return;
    const overrideCount = Object.keys(farmOverrides).length;
    modal.confirm({
      title: "¿Confirmar importación?",
      content: `Se importarán ${preview.total} registros${overrideCount > 0 ? ` (${overrideCount} con asignación manual)` : ""}. Los existentes serán sobreescritos.`,
      okText: "Importar",
      cancelText: "Cancelar",
      onOk: async () => {
        setLoadingImport(true);
        try {
          const token = localStorage.getItem("access_token") || "";
          // Build overrides map: "farmId-seasonYear" → dbFarmId
          const overridesMap: Record<string, number> = {};
          for (const [key, val] of Object.entries(farmOverrides)) {
            overridesMap[key] = val.farmId;
          }
          const body = {
            rows: jsonData,
            _farm_overrides: overridesMap,
          };
          const res = await fetch(`${API_BASE_URL}/farm-production-records/confirm-import/`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(body),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const result = await res.json();
          message.success(
            `Importación completada: ${result.created} creados, ${result.updated} actualizados, ${result.errors} errores`
          );
          navigate("/farm-production");
        } catch (err) {
          message.error("Error al importar: " + String(err));
        } finally {
          setLoadingImport(false);
        }
      },
    });
  };

  const searchFarms = useCallback((query: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!query || query.length < 2) {
      setFarmOptions([]);
      return;
    }
    setFarmSearchLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem("access_token") || "";
        const res = await fetch(
          `${API_BASE_URL}/farms/?search=${encodeURIComponent(query)}&page_size=20`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error();
        const data = await res.json();
        const results = data?.results || data?.data || (Array.isArray(data) ? data : []);
        setFarmOptions(results);
      } catch {
        setFarmOptions([]);
      } finally {
        setFarmSearchLoading(false);
      }
    }, 300);
  }, []);

  const handleFarmOverride = (row: ImportPreviewRow, farmId: number) => {
    const farm = farmOptions.find((f) => f.id === farmId);
    if (!farm) return;
    const key = `${row.external_farm_id}-${row.season_year}`;
    setFarmOverrides((prev) => ({
      ...prev,
      [key]: {
        farmId: farm.id,
        farmName: farm.name,
        producerName: farm.producer?.name || "",
      },
    }));
  };

  const clearFarmOverride = (row: ImportPreviewRow) => {
    const key = `${row.external_farm_id}-${row.season_year}`;
    setFarmOverrides((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const getRowOverride = (row: ImportPreviewRow): FarmOverride | undefined => {
    return farmOverrides[`${row.external_farm_id}-${row.season_year}`];
  };

  // Compute stats including overrides
  const overrideCount = Object.keys(farmOverrides).length;
  const effectiveUnresolved = preview
    ? preview.rows.filter((r) => {
        if (getRowOverride(r)) return false;
        return !r.farm_resolved;
      }).length
    : 0;

  const displayedRows = preview
    ? filterMode === "issues"
      ? preview.rows.filter((r) => r.issues.length > 0 || !r.farm_resolved)
      : preview.rows
    : [];

  // Track which row is being edited: "externalFarmId-seasonYear" or null
  const [editingRowKey, setEditingRowKey] = useState<string | null>(null);

  const renderFarmAssignCell = (_: unknown, row: ImportPreviewRow) => {
    const override = getRowOverride(row);
    const rowKey = `${row.external_farm_id}-${row.season_year}`;
    const isEditing = editingRowKey === rowKey;

    if (override && !isEditing) {
      return (
        <Space size={4}>
          <Tag color="green" style={{ margin: 0 }}>
            <LinkOutlined /> {override.producerName} — {override.farmName}
          </Tag>
          <Button
            type="text"
            size="small"
            onClick={() => setEditingRowKey(rowKey)}
            icon={<EditOutlined />}
            style={{ padding: "0 4px" }}
          />
          <Button
            type="text"
            size="small"
            danger
            onClick={() => clearFarmOverride(row)}
            style={{ padding: "0 4px" }}
          >
            ✕
          </Button>
        </Space>
      );
    }

    if (isEditing) {
      return (
        <Space size={4} style={{ width: "100%" }}>
          <Select
            showSearch
            autoFocus
            placeholder="Buscar productor o finca..."
            filterOption={false}
            onSearch={searchFarms}
            onChange={(val: number) => {
              handleFarmOverride(row, val);
              setEditingRowKey(null);
            }}
            loading={farmSearchLoading}
            style={{ width: 280 }}
            popupMatchSelectWidth={350}
            getPopupContainer={() => document.body}
            notFoundContent={farmSearchLoading ? "Buscando..." : "Escribe para buscar"}
            options={farmOptions.map((f) => ({
              value: f.id,
              label: f.display_name,
            }))}
          />
          <Button
            type="text"
            size="small"
            onClick={() => setEditingRowKey(null)}
            style={{ padding: "0 4px" }}
          >
            ✕
          </Button>
        </Space>
      );
    }

    return (
      <Space size={4}>
        {row.farm_resolved ? (
          <Text type="secondary">{row.farm_db_producer} — {row.farm_db_name}</Text>
        ) : (
          <Text type="danger">Sin asignar</Text>
        )}
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={() => {
            setFarmOptions([]);
            setEditingRowKey(rowKey);
          }}
          style={{ padding: 0 }}
        >
          {row.farm_resolved ? "Cambiar" : "Asignar"}
        </Button>
      </Space>
    );
  };

  const columns = [
    {
      title: "Estado",
      key: "status",
      width: 70,
      render: (_: unknown, row: ImportPreviewRow) => {
        const override = getRowOverride(row);
        if (override)
          return <Tooltip title="Asignación manual"><CheckCircleOutlined style={{ color: "#52c41a", fontSize: 18 }} /></Tooltip>;
        if (!row.farm_resolved)
          return <Tooltip title={row.issues.join(" | ")}><CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 18 }} /></Tooltip>;
        if (row.match_method && row.match_method.startsWith("by_name"))
          return <Tooltip title={row.issues.join(" | ")}><WarningOutlined style={{ color: "#1677ff", fontSize: 18 }} /></Tooltip>;
        if (row.producer_name_mismatch)
          return <Tooltip title={row.issues.join(" | ")}><WarningOutlined style={{ color: "#faad14", fontSize: 18 }} /></Tooltip>;
        return <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 18 }} />;
      },
    },
    { title: "Año", dataIndex: "season_year", key: "season_year", width: 70 },
    { title: "Productor (JSON)", dataIndex: "producer_name", key: "producer_name", ellipsis: true, width: 180 },
    { title: "Finca (JSON)", dataIndex: "farm_name", key: "farm_name", ellipsis: true, width: 150 },
    {
      title: "Finca asignada (DB)",
      key: "farm_assigned",
      width: 300,
      render: renderFarmAssignCell,
    },
    { title: "Provincia", dataIndex: "province_name", key: "province_name", width: 120 },
    {
      title: "Estimado (kg)",
      dataIndex: "audited_kg",
      key: "audited_kg",
      width: 120,
      render: (v: number | null) => v != null ? v.toLocaleString("es-ES") : "—",
    },
    {
      title: "Producido (kg)",
      dataIndex: "produced_kg",
      key: "produced_kg",
      width: 120,
      render: (v: number | null) => v != null ? v.toLocaleString("es-ES") : "—",
    },
    {
      title: "Info",
      key: "issues",
      width: 200,
      render: (_: unknown, row: ImportPreviewRow) => {
        const override = getRowOverride(row);
        if (override) {
          return <Tag color="green">Asignación manual</Tag>;
        }
        return row.issues.length > 0
          ? row.issues.map((issue, i) => (
              <Tag
                color={row.farm_resolved ? "blue" : "red"}
                key={i}
                style={{ whiteSpace: "normal" }}
              >
                {issue}
              </Tag>
            ))
          : null;
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Importar datos de producción</Title>
          <Text type="secondary">Sube un archivo JSON con los registros de producción por campaña</Text>
        </div>

        <Card>
          <Space>
            <Upload
              accept=".json"
              maxCount={1}
              fileList={fileList}
              beforeUpload={(file) => {
                setFileList([file as unknown as UploadFile]);
                handleFileRead(file);
                return false;
              }}
              onRemove={() => { setFileList([]); setJsonData(null); setPreview(null); setFarmOverrides({}); }}
            >
              <Button icon={<UploadOutlined />}>Seleccionar archivo JSON</Button>
            </Upload>
            <Button
              type="default"
              onClick={handlePreview}
              disabled={!jsonData}
              loading={loadingPreview}
            >
              Previsualizar
            </Button>
          </Space>
        </Card>

        {preview && (
          <>
            <Row gutter={16}>
              <Col span={4}>
                <Card><Statistic title="Total" value={preview.total} /></Card>
              </Col>
              <Col span={4}>
                <Card>
                  <Statistic
                    title="Resueltas"
                    value={preview.resolved_farms}
                    valueStyle={{ color: "#52c41a" }}
                  />
                </Card>
              </Col>
              <Col span={4}>
                <Card>
                  <Statistic
                    title="Por nombre"
                    value={preview.resolved_by_name}
                    valueStyle={{ color: "#1677ff" }}
                  />
                </Card>
              </Col>
              <Col span={4}>
                <Card>
                  <Statistic
                    title="Manuales"
                    value={overrideCount}
                    valueStyle={{ color: overrideCount > 0 ? "#52c41a" : undefined }}
                  />
                </Card>
              </Col>
              <Col span={4}>
                <Card>
                  <Statistic
                    title="Sin resolver"
                    value={effectiveUnresolved}
                    valueStyle={effectiveUnresolved > 0 ? { color: "#ff4d4f" } : { color: "#52c41a" }}
                  />
                </Card>
              </Col>
              <Col span={4}>
                <Card>
                  <Statistic
                    title="Discrepancias"
                    value={preview.producer_mismatches}
                    valueStyle={{ color: preview.producer_mismatches > 0 ? "#faad14" : "#52c41a" }}
                  />
                </Card>
              </Col>
            </Row>

            {effectiveUnresolved > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`${effectiveUnresolved} fincas sin resolver. Puedes asignarlas manualmente con el botón "Asignar" en cada fila, o se importarán con farm=NULL.`}
              />
            )}

            <Divider />

            <Space>
              <Button
                type={filterMode === "all" ? "primary" : "default"}
                onClick={() => setFilterMode("all")}
              >
                Todos ({preview.total})
              </Button>
              <Button
                type={filterMode === "issues" ? "primary" : "default"}
                danger={filterMode === "issues"}
                onClick={() => setFilterMode("issues")}
              >
                Con problemas ({preview.rows.filter((r) => r.issues.length > 0 || !r.farm_resolved).length})
              </Button>
              <Button
                type="primary"
                icon={<ImportOutlined />}
                onClick={handleConfirmImport}
                loading={loadingImport}
              >
                Confirmar importación
              </Button>
            </Space>

            <Table
              dataSource={displayedRows}
              columns={columns}
              rowKey={(r) => `${r.external_farm_id}-${r.season_year}`}
              size="small"
              pagination={{ pageSize: 50, showSizeChanger: true }}
              scroll={{ x: 1400 }}
              rowClassName={(r) => {
                if (getRowOverride(r)) return "";
                if (!r.farm_resolved) return "row-error";
                if (r.producer_name_mismatch) return "row-warning";
                return "";
              }}
            />
          </>
        )}
      </Space>
    </div>
  );
};
